import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Loader2, ArrowDownToLine, ArrowUpFromLine, Check, X, Clock,
  AlertTriangle, RefreshCw, ExternalLink, Eye,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useChatLayout } from '../context/ChatLayoutContext';
/* ⚠️ Sayı biçimi TEK YERDEN. Paneller `fmtFull` kullanıyor; burada ayrı
   bir kopya tutmak, biri değişince aynı tutarın iki ekranda farklı
   görünmesi demekti. */
import { fmtFull as fmt } from '../components/mm2/ItemVisuals';
import { Mm2Logo } from '../components/mm2/Mm2Brand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

/* ============================================================================
   EŞYA İŞLEMLERİ  —  MM2 yatırma / çekme geçmişi
   ----------------------------------------------------------------------------
   ⚠️ BU SAYFA YENİDEN YAZILDI. Eskiden Robux P2P çekimlerini listeliyordu ve
      iki uca bağlıydı: /game/withdrawals ve /game/withdraw/cancel. O ray
      söküldüğü için ikisi de artık 404 döner — sayfa menüde BAĞLI olduğundan
      kullanıcı tıklayıp boş/bozuk bir ekran görürdü.
      Yerine MM2 rayının kendi geçmişi geldi: /api/game/mm2/trades.

   ⚠️ İLAN DOLULUK ÇUBUĞU (FillBar) KALDIRILDI. O, P2P'ye özgü bir kavramdı:
      bir çekim ilanı birden çok alıcı tarafından parça parça doluyordu.
      MM2'de böyle bir şey yok — bot ya takası yapar ya yapmaz. Çubuğu
      taşımak, olmayan bir mekaniği varmış gibi göstermek olurdu.
============================================================================ */

const STATUS = {
  queued:       { label: 'Waiting for bot', tone: 'text-amber-300', bg: 'bg-amber-500/10', br: 'border-amber-500/25', Icon: Clock },
  sent:         { label: 'Trade sent',      tone: 'text-sky-300',   bg: 'bg-sky-500/10',   br: 'border-sky-500/25',   Icon: ExternalLink },
  settling:     { label: 'Finalising',      tone: 'text-sky-300',   bg: 'bg-sky-500/10',   br: 'border-sky-500/25',   Icon: Loader2 },
  waiting_leg:  { label: 'Queued (step)',   tone: 'text-zinc-300',  bg: 'bg-white/5',      br: 'border-[#2A2D31]',    Icon: Clock },
  needs_review: { label: 'Under review',    tone: 'text-amber-300', bg: 'bg-amber-500/10', br: 'border-amber-500/25', Icon: Eye },
  done:         { label: 'Completed',       tone: 'text-[#2ecf6e]', bg: 'bg-[#16A34A]/10', br: 'border-[#16A34A]/25', Icon: Check },
  failed:       { label: 'Failed',          tone: 'text-rose-300',  bg: 'bg-rose-500/10',  br: 'border-rose-500/25',  Icon: AlertTriangle },
  cancelled:    { label: 'Cancelled',       tone: 'text-zinc-400',  bg: 'bg-white/5',      br: 'border-[#2A2D31]',    Icon: X },
};

/* Devam eden sayılan durumlar — sunucudaki _MM2_ACTIVE_TRADE_STATES ile AYNI.
   ⚠️ Ayrışırsa: burada "bitti" görünen bir işlemin eşyaları sunucuda hâlâ
   kilitli olur ve kullanıcı neden yeni işlem açamadığını anlamaz. */
const LIVE = new Set(['queued', 'sent', 'settling', 'waiting_leg', 'needs_review']);



const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

/* ------------------------------------------------------------------ KART */
const TradeCard = ({ t, onCancel, busy }) => {
  const st = STATUS[t.status] || STATUS.cancelled;
  const isDep = t.type === 'deposit';
  const DirIcon = isDep ? ArrowDownToLine : ArrowUpFromLine;
  const items = t.item_names || [];
  /* ⚠️ İPTAL YALNIZCA 'queued' İKEN. Bot göreve başladıktan sonra sunucu
     iptali reddeder; düğmeyi göstermek tutulamayacak bir söz vermek olurdu. */
  const canCancel = t.status === 'queued';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border ${st.br} bg-[#1A1D20] p-4 space-y-3 shadow-lg`}
      data-testid={`trade-${t.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${st.bg}`}>
            <DirIcon className={`w-4 h-4 ${st.tone}`} />
          </span>
          <div className="min-w-0">
            <p className="font-heading font-bold text-white text-base leading-tight">
              {isDep ? 'Item deposit' : 'Item withdrawal'}
            </p>
            <p className="text-[11px] text-zinc-500 mt-0.5">{fmtDate(t.created_at)}</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 shrink-0 text-[10px] font-black
          uppercase tracking-wider px-2 py-1 rounded-lg ${st.bg} ${st.tone}`}>
          <st.Icon className={`w-3 h-3 ${t.status === 'settling' ? 'animate-spin' : ''}`} />
          {st.label}
        </span>
      </div>

      {!!items.length && (
        <div className="flex flex-wrap gap-1.5">
          {items.slice(0, 8).map((n, i) => (
            <span key={i} className="text-[11px] font-bold text-zinc-300 bg-[#0E0F11]
                                     border border-[#22252a] rounded-lg px-2 py-1">
              {n}
            </span>
          ))}
          {items.length > 8 && (
            <span className="text-[11px] font-bold text-zinc-500 px-1 py-1">
              +{items.length - 8} more
            </span>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
        {Number(t.value) > 0 && (
          <span className="text-zinc-500">
            Item value <strong className="text-zinc-300">{fmt(t.value)}</strong>
          </span>
        )}
        {Number(t.leg_total) > 1 && (
          <span className="text-zinc-500">
            Step <strong className="text-zinc-300">
              {(Number(t.leg_index) || 0) + 1}/{t.leg_total}
            </strong>
          </span>
        )}
        {t.bot && (t.bot.roblox_username || t.bot.name) && (
          <span className="text-zinc-500 inline-flex items-center gap-1.5">
            <span className={`w-1.5 h-1.5 rounded-full ${t.bot_online
              ? 'bg-[#2ecf6e]' : 'bg-zinc-600'}`} />
            {t.bot.roblox_username || t.bot.name}
          </span>
        )}
      </div>

      {/* ⚠️ HATA SEBEBİ GÖSTERİLİR. Sunucu `error` alanını dolduruyor; gizlemek
          kullanıcıyı "neden olmadı?" sorusuyla desteğe yollar. */}
      {t.status === 'failed' && t.error && (
        <p className="text-[11px] text-rose-300/85 leading-relaxed">
          {String(t.error).replace(/_/g, ' ')}
        </p>
      )}
      {t.status === 'needs_review' && (
        <p className="text-[11px] text-amber-200/85 leading-relaxed">
          We could not confirm this trade automatically. Support is checking it —
          your items and balance are safe in the meantime.
        </p>
      )}

      {LIVE.has(t.status) && t.status !== 'needs_review'
        && t.bot && t.bot.private_server_link && (
        <a href={t.bot.private_server_link} target="_blank" rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl
                     bg-[#7b2fc4] hover:bg-[#B059E8] text-white font-bold text-xs
                     transition-colors">
          <ExternalLink className="w-3.5 h-3.5" /> Join bot server
        </a>
      )}

      {canCancel && (
        <button onClick={() => onCancel(t.id)} disabled={busy}
          data-testid={`cancel-${t.id}`}
          className="w-full py-2.5 rounded-xl bg-[#0E0F11] border border-[#2A2D31]
                     hover:border-zinc-600 text-zinc-400 text-xs font-bold
                     transition-colors disabled:opacity-50 inline-flex items-center
                     justify-center gap-2">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
          {isDep ? 'Cancel' : 'Cancel & refund'}
        </button>
      )}
    </motion.div>
  );
};

/* ------------------------------------------------------------------ SAYFA */
export const Withdraws = () => {
  // 💬 Aç/kapa durumu paylaşımlı: sayfa değişse de korunur.
  const { chatOpen } = useChatLayout();
  const { token, refreshUser } = useAuth();
  const [rows, setRows] = useState(null);      // null = ilk yükleme
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!token) { setRows([]); return; }
    try {
      const res = await axios.get(`${API}/game/mm2/trades`,
        { headers: { Authorization: `Bearer ${token}` } });
      setRows(Array.isArray(res.data && res.data.trades) ? res.data.trades : []);
    } catch {
      setRows([]);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const active = useMemo(
    () => (rows || []).filter(r => LIVE.has(r.status)), [rows]);
  const past = useMemo(
    () => (rows || []).filter(r => !LIVE.has(r.status)), [rows]);

  /* ⚠️ YOKLAMA YALNIZCA DEVAM EDEN İŞLEM VARKEN. Yalnızca geçmişe bakan bir
     kullanıcı için 8 saniyede bir istek atmak boşuna yük. */
  useEffect(() => {
    if (!active.length) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [active.length, load]);

  const cancel = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/game/mm2/trade/cancel`, { trade_id: id },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Cancelled.');
      await load();
      if (refreshUser) refreshUser();
    } catch (error) {
      const detail = error && error.response && error.response.data
        ? error.response.data.detail : null;
      toast.error(typeof detail === 'string' ? detail : 'Could not cancel.');
      /* ⚠️ HATA SONRASI DA TAZELE. En sık sebep "bot çoktan başladı";
         listeyi yenilemek kullanıcının artık iptal edilemez durumu
         görmesini sağlar, aksi halde aynı düğmeye tekrar basar. */
      load();
    } finally {
      setBusyId(null);
    }
  };

  if (rows === null) {
    return (
      <div className="min-h-screen bg-[#0B0C0E] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#B059E8] animate-spin" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-[#0B0C0E] transition-all duration-300
                     ${chatOpen ? 'lg:pr-[320px]' : ''}`}>
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">

        {/* Başlık, sitenin geri kalanıyla AYNI iskelette: logo + üst etiket,
            sonra başlık. Sayfadan sayfaya farklı bir başlık düzeni kurmak,
            kullanıcının her ekranda yeniden yön bulmasına yol açıyor. */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 mb-2.5">
              <Mm2Logo size={16} />
              <span className="mm-eyebrow">Murder Mystery 2</span>
            </div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-white
                           uppercase tracking-wide leading-none">
              Item transfers
            </h1>
            <p className="text-sm text-zinc-500 mt-2.5">
              Every trade between you and our bots, newest first.
            </p>
          </div>
          <button onClick={load} aria-label="Refresh"
            className="mm-focus shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl
                       bg-[#15171a] border border-[#2A2D31] hover:border-[#3a3d43]
                       text-zinc-400 hover:text-white text-xs font-bold uppercase
                       tracking-wider transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {rows.length === 0 && (
          <div className="rounded-[14px] border border-dashed border-[#2A2D31] py-16
                          grid place-items-center text-center px-6">
            <Mm2Logo size={44} className="mb-4 opacity-35" alt="" />
            <p className="font-heading font-bold text-zinc-300 text-lg">
              No item transfers yet
            </p>
            <p className="text-xs text-zinc-400 mt-1 max-w-[300px] leading-relaxed">
              Deposit MM2 items to get MM Coin, or spend your balance on items
              from our bot stock.
            </p>
          </div>
        )}

        {active.length > 0 && (
          <section className="space-y-3">
            <h2 className="mm-eyebrow">
              In progress
            </h2>
            {active.map(t => (
              <TradeCard key={t.id} t={t} onCancel={cancel} busy={busyId === t.id} />
            ))}
          </section>
        )}

        {past.length > 0 && (
          <section className="space-y-3">
            <h2 className="mm-eyebrow">
              History
            </h2>
            {past.map(t => (
              <TradeCard key={t.id} t={t} onCancel={cancel} busy={busyId === t.id} />
            ))}
          </section>
        )}
      </div>
    </div>
  );
};

export default Withdraws;
